import { useTranslations } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { useCallback, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import z from 'zod/v4'
import { DatePicker } from '~/components/DatePicker'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper.tsx'
import { ViewTitle } from '~/components/layout/Sections.tsx'
import { Detail, DisplayValue, Label } from '~/components/typography/Content.tsx'
import { Calendar } from '~/components/ui/calendar'
import { Input } from '~/components/ui/input'
import { getValidDate } from '~/lib/utils.ts'

export const datesSchema = z.object({
  listingDate: z.string().min(1),
  saleDate: z.string().optional(),
  listingLink: z.string().optional(),
})

export type DatesData = z.infer<typeof datesSchema>

export interface DatesPhaseProps {
  relationType?: 'representation' | 'reference' | 'prospect'
  dateValues: DatesData
  onUpdateData: (data: DatesData) => void
}

const maxDate = dayjs().endOf('day').toDate()
const minDate = dayjs().subtract(10, 'year').toDate()

export function DatesPhase({ relationType, dateValues, onUpdateData }: DatesPhaseProps) {
  const { t } = useTranslations('property')

  const currentListingDate = useMemo(
    () => getValidDate(dateValues?.listingDate),
    [dateValues?.listingDate]
  )

  const currentSaleDate = useMemo(() => getValidDate(dateValues?.saleDate), [dateValues?.saleDate])

  const existingDateInput = useRef<'listing' | 'sale' | null>(
    currentListingDate ? 'listing' : currentSaleDate ? 'sale' : null
  )

  const [showDaysInput, setShowDaysInput] = useState(!currentSaleDate || !currentListingDate)

  const onUpdateValue = useCallback(
    (key: keyof DatesData, value: string) => {
      onUpdateData({
        ...dateValues,
        [key]: value,
      })
    },
    [onUpdateData, dateValues]
  )

  const daysOnMarket = useMemo(() => {
    if (!currentListingDate || !currentSaleDate) {
      return 0
    }

    return dayjs(currentSaleDate).diff(dayjs(currentListingDate), 'day')
  }, [currentListingDate, currentSaleDate])

  const [daysOnMarketInput, _setDaysOnMarketInput] = useState(daysOnMarket.toString())

  const setDaysOnMarketInput = useCallback(
    (text: string) => {
      existingDateInput.current = currentListingDate ? 'listing' : currentSaleDate ? 'sale' : null

      _setDaysOnMarketInput(text)
    },
    [currentListingDate, currentSaleDate]
  )

  return (
    <PhaseWrapper>
      <ViewTitle>{t(($) => $.flow.dates.title)}</ViewTitle>

      <View className="flex-1 gap-8">
        <View className="gap-2">
          <Label>{t(($) => $.flow.dates.listingDate)}</Label>
          <Calendar
            maxDate={maxDate}
            minDate={minDate}
            date={currentListingDate ? new Date(currentListingDate) : undefined}
            timePicker={false}
            onChange={(date) => {
              const nextListingDate = dayjs(date)

              if (!nextListingDate.isValid()) {
                console.log('invalid listing date')
                return
              }

              onUpdateValue('listingDate', nextListingDate.format('YYYY-MM-DD'))

              if (date && !existingDateInput.current) {
                existingDateInput.current = 'listing'
              }

              if (!date && existingDateInput.current === 'listing') {
                existingDateInput.current = null

                if (currentSaleDate) {
                  setShowDaysInput(true)
                }
              }

              if (date && currentSaleDate) {
                setShowDaysInput(false)
              }
            }}
          />
          <Detail>{t(($) => $.flow.dates.listingDateDescription)}</Detail>
        </View>

        {relationType === 'reference' && (
          <>
            <View className="gap-2">
              <Label>{t(($) => $.flow.dates.saleDate)}</Label>
              <DatePicker
                maxDate={maxDate}
                minDate={currentListingDate || minDate}
                value={currentSaleDate ? new Date(currentSaleDate) : undefined}
                onDateChange={(date) => {
                  const nextSaleDate = dayjs(date)

                  if (!nextSaleDate.isValid()) {
                    console.log('invalid sale date')
                    return
                  }

                  onUpdateValue('saleDate', nextSaleDate.format('YYYY-MM-DD'))

                  if (date && !existingDateInput.current) {
                    existingDateInput.current = 'sale'
                  }

                  if (!date && existingDateInput.current === 'sale') {
                    existingDateInput.current = null

                    if (currentListingDate) {
                      setShowDaysInput(true)
                    }
                  }

                  if (date && currentListingDate) {
                    setShowDaysInput(false)
                  }
                }}
              />
              <Detail>{t(($) => $.flow.dates.saleDateDescription)}</Detail>
            </View>
            {(!!currentListingDate || !!currentSaleDate) && (
              <View className="gap-2">
                <Label>{t(($) => $.flow.dates.daysOnMarket)}</Label>
                {!showDaysInput ? (
                  <DisplayValue>
                    {daysOnMarket} {t(($) => $.flow.dates.days)}
                  </DisplayValue>
                ) : (
                  <Input
                    placeholder={t(($) => $.flow.dates.daysOnMarketPlaceholder)}
                    keyboardType="numeric"
                    value={daysOnMarketInput}
                    onSubmitEditing={() => {
                      if (currentListingDate && !currentSaleDate) {
                        setShowDaysInput(false)
                      }
                    }}
                    onChangeText={(text) => {
                      setDaysOnMarketInput(text)
                      const days = Number.parseInt(text, 10)

                      if (!Number.isNaN(days)) {
                        if (existingDateInput.current === 'sale') {
                          onUpdateValue(
                            'listingDate',
                            dayjs(currentListingDate).add(days, 'day').format('YYYY-MM-DD')
                          )
                        } else {
                          onUpdateValue(
                            'saleDate',
                            dayjs(currentSaleDate).subtract(days, 'day').format('YYYY-MM-DD')
                          )
                        }
                      }
                    }}
                  />
                )}
              </View>
            )}
          </>
        )}
      </View>
    </PhaseWrapper>
  )
}
